import { Coordinate, FuelUpEvent } from "@fairfleet/geotab";
import { EnergyDevice } from "./energyDevice";
import { reportedNonZero } from "./reportedValue";
import { TripContext } from "./tripContext";
import { driverIdOf } from "@/hooks/users/useDrivers";
import { reportedNumber } from "@/utilities/reportedNumber";
import { toCoordinate } from "@/utilities/toCoordinate";

export interface FuelUpEventRow {
  id: string;
  deviceId: string;
  name: string;
  licensePlate: string;
  /** When the tank level jumped. `null` when Geotab reported no usable time for the fill-up. */
  dateTime: Date | null;
  /** Volume derived from the tank level change, in litres. */
  volumeLiters: number | null;
  /** Volume off the fuel card transaction, when there is one. */
  cardVolumeLiters: number | null;
  /** The total-fuel-used counter at the fill-up, in litres. */
  totalFuelUsedLiters: number | null;
  /** How far apart fuel in and fuel out were, as Geotab rates it. */
  deviationFactor: number | null;
  /** The driver as an id: the entity references one without naming it. */
  driverId: string | null;
  odometerKm: number | null;
  location: Coordinate | null;
  /**
   * Whether a fuel card transaction backs this fill-up. Without one there is no receipt for the
   * volume, and the odometer and the position are taken from the surrounding trip instead.
   */
  hasTransaction: boolean;
}

/**
 * Turns the fill-up events of one vehicle into rows.
 *
 * A fill-up reaches Geotab in one of two ways: read off a fuel card transaction, which brings its
 * own volume, odometer and station position, or detected from the tank level rising, which brings
 * none of those. For the detected ones the surrounding trip answers where the vehicle was and
 * what the odometer read, so the row is filled from there and marked as such rather than left
 * blank.
 *
 * @param device - The vehicle the events belong to.
 * @param events - The fill-up events as Geotab returned them.
 * @param tripAt - Resolves what the vehicle was doing at a given moment.
 * @returns The rows, in the order Geotab returned them.
 */
export function toFuelUpEventRows(
  device: EnergyDevice,
  events: FuelUpEvent[],
  tripAt: (time: number) => TripContext,
): FuelUpEventRow[] {
  return events.map((event, index) => {
    // The tank level maximum is the moment the tank was full; the minimum is where filling
    // started. Either dates the fill-up, the maximum more precisely. When no tank level
    // extrema exist (card-only fill-up) the transaction's own dateTime is the next best.
    const transaction = event.fuelTransactions?.[0];
    const level = event.tankLevelExtrema?.maxima ?? event.tankLevelExtrema?.minima;
    const rawTime = level?.dateTime ?? transaction?.dateTime;
    const parsed = rawTime ? new Date(rawTime) : null;
    const dateTime = parsed && !isNaN(parsed.getTime()) ? parsed : null;

    const cardOdometerKm = transaction ? reportedNonZero(transaction.odometer) : null;
    const cardLocation = transaction ? toCoordinate(transaction.location) : null;
    const cardDriverId = transaction ? driverIdOf(transaction.driver) : null;
    const context = dateTime ? tripAt(dateTime.getTime()) : null;

    return {
      // The index is always part of the id: FuelUpEvent carries none of its own, and two
      // fill-ups can share a timestamp or lack one altogether.
      id: `${device.id}-${index}-${dateTime?.getTime() ?? "unknown"}`,
      deviceId: device.id,
      name: device.name,
      licensePlate: device.licensePlate,
      dateTime,
      volumeLiters: reportedNonZero(event.derivedVolume),
      cardVolumeLiters: transaction ? reportedNonZero(transaction.volume) : null,
      totalFuelUsedLiters: reportedNumber(event.totalFuelUsed),
      deviationFactor: reportedNumber(event.deviationFactor),
      driverId: cardDriverId ?? context?.driverId ?? null,
      odometerKm: cardOdometerKm ?? context?.odometerKm ?? null,
      location: cardLocation ?? context?.location ?? null,
      hasTransaction: !!transaction,
    };
  });
}
